import { Person, PersonFilters, PersonFieldLabels, LoginTypes, LoginTypesTranslator } from "../../api/Person";
import { SelectFormField } from "../../common/formfields/SelectFormField";
import { DefinedPropertyObject, IEntityMinimalModel, ISearchTermParametersDefaults } from "../../api/GenericTypes";
import { ToggleFormField } from "../../common/formfields/ToggleFormField";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { RolesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/RolesVirtualizedSelectForm";
import { EntityFilterSideBar } from "../../common/sidebar/filtersidebar/EntityFilterBar";
import { useContext } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import {
  CustomFieldFilterLayout,
  CustomFieldFilterLayoutForm,
} from "../../Customization/CustomFieldFilters/CustomFieldFilterLayout/CustomFieldFilterLayout";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { FoldableFilters } from "../../common/sidebar/filtersidebar/GenericFilters/common/FoldableFilters";

type AccountStatus = { id: number; name: string; value: boolean };

const AccountStatusOptions = [
  { id: 1, name: "no account", value: false },
  { id: 2, name: "has account", value: true },
  { id: 3, name: "enabled", value: true },
  { id: 4, name: "disabled", value: false },
] as AccountStatus[];

const LoginTypesOptions: { name: string; value: LoginTypes }[] = [
  { name: LoginTypesTranslator["Session"], value: "Session" },
  { name: LoginTypesTranslator["SSO"], value: "SSO" },
  { name: LoginTypesTranslator["LDAP"], value: "LDAP" },
];

export interface PersonFilterForm
  extends ICombinedModificationRecordEntityForm,
    ISoftDeletableEntityForm,
    CustomFieldFilterLayoutForm {
  roleIds?: IEntityMinimalModel[] | undefined | null;
  accountState?: AccountStatus | undefined | null;
  loginTypes?: { label: string; value: LoginTypes }[] | undefined | null;
  includeSystemUsers?: boolean | undefined | null;
  customType?: { id: number; name: string }[] | undefined | null;
}

const defaultValues: DefinedPropertyObject<PersonFilterForm> = {
  roleIds: null,
  accountState: null,
  loginTypes: null,
  includeSystemUsers: false,
  customType: null,
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
  customFieldFilters: [],
};
export const personsFormToFilters = (form: PersonFilterForm): PersonFilters => {
  let filters: PersonFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };
  filters.roleIds = Array.isArray(form.roleIds) && form.roleIds.length > 0 ? form.roleIds?.map((d) => d.id) : null;
  filters.hasAccount =
    form.accountState !== undefined && form.accountState !== null
      ? form.accountState.id <= 2
        ? form.accountState.value
        : null
      : null;
  filters.isAccountEnabled =
    form.accountState !== undefined && form.accountState !== null
      ? form.accountState.id > 2
        ? form.accountState.value
        : null
      : null;
  filters.loginTypes =
    Array.isArray(form.loginTypes) && form.loginTypes.length > 0 ? form.loginTypes.map((d) => d.value) : null;
  filters.includeSystemUsers = form.includeSystemUsers || false;
  filters.customTypeIds =
    Array.isArray(form.customType) && form.customType.length > 0 ? form.customType.map((d) => d.id) : null;
  return filters;
};

/**
 * Converts PersonFilters to PersonFilterForm
 * @param filters PersonFilters
 * @returns PersonFilterForm
 * @author @CorradoSurmanowicz
 */
export const filtersToPersonsForm = (filters: PersonFilters): PersonFilterForm => {
  return {
    roleIds: filters.roleIds?.map((id) => ({ id, name: "" })) || null,
    accountState:
      filters.hasAccount !== null ? AccountStatusOptions.find((opt) => opt.value === filters.hasAccount) : null,
    loginTypes: filters.loginTypes
      ? filters.loginTypes.map((lt) => ({ label: LoginTypesTranslator[lt], value: lt }))
      : null,
    includeSystemUsers: filters.includeSystemUsers || false,
    customType: filters.customTypeIds?.map((id) => ({ id, name: "" })) || null,
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};
const defaultFilters: PersonFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
  ...ISearchTermParametersDefaults,
};

export const PersonFilterBar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<Person, PersonFilterForm, PersonFilters>) => {
  const { session } = useContext(SessionContext);
  // const stateArray = [
  //   !!initialValues?.customType?.length,
  //   !!initialValues?.roleIds?.length,
  //   initialValues?.accountState !== null && initialValues?.accountState !== undefined,
  //   initialValues?.loginTypes !== null && initialValues?.loginTypes !== undefined,
  //   initialValues?.includeSystemUsers === true,
  // ];

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={defaultValues}
      defaultFilters={defaultFilters}
      formToFilters={personsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
      customTypeConstraint="Person"
    >
      {({ control, register, errors, onReset, customFieldsDict }) => (
        <EntityFilterSideBar onReset={onReset}>
          <FoldableFilters
            label="General"
            // defaultOpen={stateArray.some((p) => p) && !!stateArray.length}
            defaultOpen
          >
            <CustomTypesVirtualizedSelectForm
              id="customType"
              control={control}
              showControls={false}
              filters={{ entityTypes: ["Person"] }}
              isMulti
              enableBasicOption
              hideIfNoTypes
            />

            {currentTab !== "activeUsers" && currentTab !== "disabledUsers" && (
              <SelectFormField
                id="accountState"
                label={PersonFieldLabels.accountState}
                {...register}
                items={AccountStatusOptions}
                control={control}
                horizontal={false}
              />
            )}

            <SelectFormField
              id="loginTypes"
              label={PersonFieldLabels.loginType}
              {...register}
              items={LoginTypesOptions}
              control={control}
              horizontal={false}
              isMulti
            />

            <RolesVirtualizedSelectForm
              id="roleIds"
              label={PersonFieldLabels.roles}
              control={control}
              isMulti
              filters={{
                includeInternals: !!session?.permissions.logsadmin || !!session?.permissions.view_admin_pages,
              }}
            />

            {(!!session?.permissions.logsadmin || !!session?.permissions.view_admin_pages) && (
              <ToggleFormField id="includeSystemUsers" control={control} label="Include system users" horizontal />
            )}
          </FoldableFilters>
          {customFieldsDict && (
            <CustomFieldFilterLayout
              control={control}
              errors={errors}
              customFieldsDict={customFieldsDict}
              entityTypes={["Person"]}
            />
          )}

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />

          {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
